/**
 * Analytics Wrapper for LessonlyAI (PostHog + Google Analytics)
 *
 * Privacy-first implementation:
 * - No session replay
 * - No student data
 * - No lesson content
 * - Counts and high-level flags only
 * - Respects teacher trust
 */

import type { PostHog } from 'posthog-js';
import type Clarity from '@microsoft/clarity';

interface AnalyticsEvent {
  [key: string]: string | number | boolean | string[] | undefined;
}

declare global {
  interface Window {
    gtag?: (command: string, ...args: any[]) => void;
    dataLayer?: any[];
  }
}

let isInitialized = false;
let posthog: PostHog | null = null;
let gaInitialized = false;
let clarityInitialized = false;
let clarity: typeof Clarity | null = null;

// Default to US Cloud ingestion endpoint
const DEFAULT_POSTHOG_HOST = 'https://us.i.posthog.com';

/**
 * Check if debug mode is enabled
 */
function isDebugEnabled(): boolean {
  return import.meta.env.VITE_ANALYTICS_DEBUG === 'true';
}

/**
 * Initialize Google Analytics
 */
function initGoogleAnalytics(): void {
  const gaId = import.meta.env.VITE_GA_ID;
  const debugEnabled = isDebugEnabled();

  if (!gaId) {
    if (debugEnabled) {
      console.log('[GA] Disabled - VITE_GA_ID not set');
    }
    return;
  }

  if (gaInitialized) {
    if (debugEnabled) {
      console.warn('[GA] Already initialized, skipping');
    }
    return;
  }

  try {
    // Create data layer
    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || [];
      window.gtag = function() {
        (window.dataLayer as any).push(arguments);
      };
      window.gtag('js', new Date());
      window.gtag('config', gaId);

      // Load gtag script async
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
      document.head.appendChild(script);

      gaInitialized = true;

      if (debugEnabled) {
        console.log('[GA] Initialized with ID:', gaId);
      }
    }
  } catch (error) {
    console.error('[GA] Failed to initialize:', error);
  }
}

/**
 * Initialize PostHog analytics
 * MUST be called once at app startup
 */
export async function initAnalytics(): Promise<void> {
  // Check if analytics is enabled via environment variable (strict string comparison)
  const analyticsEnabled = import.meta.env.VITE_ANALYTICS_ENABLED === 'true';
  const debugEnabled = isDebugEnabled();

  if (!analyticsEnabled) {
    if (debugEnabled) {
      console.log('[Analytics] Disabled - VITE_ANALYTICS_ENABLED not set to "true"');
    }
    return;
  }

  // Prevent double initialization
  if (isInitialized) {
    if (debugEnabled) {
      console.warn('[Analytics] Already initialized, skipping');
    }
    return;
  }

  const posthogKey = import.meta.env.VITE_POSTHOG_KEY;
  const posthogHost = import.meta.env.VITE_POSTHOG_HOST || DEFAULT_POSTHOG_HOST;

  if (!posthogKey) {
    console.error('[Analytics] Missing PostHog API key (VITE_POSTHOG_KEY)');
    return;
  }

  try {
    const posthogModule = await import('posthog-js');
    posthog = posthogModule.default;

    posthog.init(posthogKey, {
      api_host: posthogHost,

      // Privacy settings - CRITICAL
      autocapture: false, // Disable automatic event tracking (manual capture only)
      capture_pageview: true, // Track pageviews for analytics
      capture_pageleave: true, // Track pageleave for bounce rate & session duration
      disable_session_recording: true, // CRITICAL: Disable session replay (saves quota)
      disable_surveys: true, // No surveys popup
      person_profiles: 'identified_only', // Only track identified users did

      // Enable scroll depth tracking for content engagement
      capture_performance: true, // Enables scroll depth tracking

      // Debugging - only enabled when VITE_ANALYTICS_DEBUG=true
      debug: debugEnabled,

      // Optional: loaded callback for debug confirmation
      loaded: debugEnabled
        ? () => {
            console.log('[Analytics] PostHog initialized', {
              host: posthogHost,
              debug: debugEnabled,
            });
          }
        : undefined,
    });

    isInitialized = true;

    // Expose PostHog to window for console debugging and testing
    if (typeof window !== 'undefined') {
      (window as any).posthog = posthog;
      if (debugEnabled) {
        console.log('[Analytics] PostHog exposed to window.posthog for debugging');
      }
    }
  } catch (error) {
    console.error('[Analytics] Failed to initialize PostHog:', error);
  }

  // Initialize Google Analytics
  initGoogleAnalytics();

  // Initialize Microsoft Clarity
  initClarity();
}

/**
 * Initialize Microsoft Clarity (heatmaps + session replay)
 */
async function initClarity(): Promise<void> {
  const clarityProjectId = import.meta.env.VITE_CLARITY_PROJECT_ID;
  const debugEnabled = isDebugEnabled();

  if (!clarityProjectId) {
    if (debugEnabled) {
      console.log('[Clarity] Disabled - VITE_CLARITY_PROJECT_ID not set');
    }
    return;
  }

  if (clarityInitialized) {
    if (debugEnabled) {
      console.warn('[Clarity] Already initialized, skipping');
    }
    return;
  }

  try {
    const { default: ClarityModule } = await import('@microsoft/clarity');
    ClarityModule.init(clarityProjectId);
    clarity = ClarityModule;
    clarityInitialized = true;

    if (debugEnabled) {
      console.log('[Clarity] Initialized with project ID:', clarityProjectId);
    }
  } catch (error) {
    console.error('[Clarity] Failed to initialize:', error);
  }
}

function getPosthog(): PostHog | null {
  if (!isInitialized || !posthog) {
    if (isDebugEnabled()) {
      console.warn('[Analytics] track() called but PostHog not initialized');
    }
    return null;
  }
  return posthog;
}

/**
 * Track an analytics event
 * No-ops if analytics is disabled or not initialized
 * Sends to both PostHog and Google Analytics
 */
export function track(eventName: string, properties?: AnalyticsEvent): void {
  const ph = getPosthog();

  // Only log events when debug mode is explicitly enabled
  if (isDebugEnabled()) {
    console.log('[Analytics] track:', eventName, properties);
  }

  // Send to PostHog
  if (ph) {
    try {
      ph.capture(eventName, properties);
    } catch (error) {
      console.error('[Analytics] Error tracking event to PostHog:', error);
    }
  }

  // Send to Google Analytics
  if (typeof window !== 'undefined' && window.gtag) {
    try {
      window.gtag('event', eventName, properties || {});
    } catch (error) {
      console.error('[Analytics] Error tracking event to GA:', error);
    }
  }
}

/**
 * Identify a user (optional, for correlation only)
 * Never call with PII
 * Sends user ID to both PostHog and Google Analytics
 */
export function identify(userId: string, traits?: AnalyticsEvent): void {
  const ph = getPosthog();

  if (isDebugEnabled()) {
    console.log('[Analytics] identify:', userId, traits);
  }

  // Send to PostHog
  if (ph) {
    try {
      ph.identify(userId, traits);
    } catch (error) {
      console.error('[Analytics] Error identifying user to PostHog:', error);
    }
  }

  // Send to Clarity
  if (clarity) {
    try {
      clarity.identify(userId);
    } catch (error) {
      console.error('[Analytics] Error identifying user to Clarity:', error);
    }
  }

  // Send to Google Analytics
  if (typeof window !== 'undefined' && window.gtag) {
    try {
      window.gtag('config', import.meta.env.VITE_GA_ID || '', {
        user_id: userId,
      });
    } catch (error) {
      console.error('[Analytics] Error setting user_id in GA:', error);
    }
  }
}

/**
 * Set user properties (traits)
 * Only call with non-PII data like tier, region, etc.
 * Never pass email, name, or any personally identifiable information
 * Sends user properties to both PostHog and Google Analytics
 */
export function setUserProperties(properties: AnalyticsEvent): void {
  const ph = getPosthog();

  if (isDebugEnabled()) {
    console.log('[Analytics] setUserProperties:', properties);
  }

  // Send to PostHog
  if (ph) {
    try {
      ph.setPersonProperties(properties);
    } catch (error) {
      console.error('[Analytics] Error setting user properties in PostHog:', error);
    }
  }

  // Send to Google Analytics (as custom user properties)
  if (typeof window !== 'undefined' && window.gtag) {
    try {
      const gaProperties: Record<string, any> = {};
      // Convert property names to GA format (snake_case, lowercase)
      for (const [key, value] of Object.entries(properties || {})) {
        if (value !== undefined) {
          gaProperties[key.toLowerCase()] = value;
        }
      }
      window.gtag('set', { user_properties: gaProperties });
    } catch (error) {
      console.error('[Analytics] Error setting user properties in GA:', error);
    }
  }
}

/**
 * Track an error event with structured data
 * Use for critical errors that should be monitored
 */
export function trackError(
  errorType: string,
  errorMessage: string,
  context?: {
    severity?: 'P0' | 'P1' | 'P2' | 'P3';
    source?: string;
    userId?: string;
    [key: string]: string | number | boolean | undefined;
  }
): void {
  const ph = getPosthog();

  if (isDebugEnabled()) {
    console.log('[Analytics] trackError:', errorType, errorMessage, context);
  }

  const errorData = {
    error_type: errorType,
    error_message: errorMessage,
    severity: context?.severity || 'P3',
    source: context?.source || 'unknown',
    ...context,
  };

  // Send to PostHog
  if (ph) {
    try {
      ph.capture('error_occurred', errorData);
    } catch (error) {
      console.error('[Analytics] Error tracking error to PostHog:', error);
    }
  }

  // Send to Google Analytics
  if (typeof window !== 'undefined' && window.gtag) {
    try {
      window.gtag('event', 'exception', {
        description: `${errorType}: ${errorMessage}`,
        fatal: context?.severity === 'P0',
      });
    } catch (error) {
      console.error('[Analytics] Error tracking error to GA:', error);
    }
  }
}

/**
 * Evaluate a PostHog feature flag for the current user.
 * Returns the variant key, true/false for boolean flags, or null if unavailable.
 */
export function getFeatureFlag(flagName: string): string | boolean | null {
  const ph = getPosthog();
  if (!ph) return null;

  try {
    const result = ph.getFeatureFlag(flagName) ?? null;
    if (isDebugEnabled()) {
      console.log('[Analytics] getFeatureFlag:', flagName, '→', result);
    }
    return result;
  } catch (error) {
    console.error('[Analytics] Error evaluating feature flag:', error);
    return null;
  }
}

/**
 * Register a callback to run once PostHog feature flags are loaded.
 * Falls back to calling immediately if PostHog is unavailable (use defaults).
 */
export function onFeatureFlagsLoaded(callback: () => void): void {
  const ph = getPosthog();
  if (!ph) {
    callback();
    return;
  }

  try {
    ph.onFeatureFlags(callback);
  } catch (error) {
    console.error('[Analytics] Error registering onFeatureFlags:', error);
    callback();
  }
}

/**
 * Track that a user was exposed to a specific experiment variant.
 * Call once per page load immediately after resolving the variant.
 */
export function trackVariantExposure(flagName: string, variant: string): void {
  const ph = getPosthog();
  if (!ph) return;

  if (isDebugEnabled()) {
    console.log('[Analytics] trackVariantExposure:', flagName, variant);
  }

  try {
    ph.capture('$feature_flag_called', {
      $feature_flag: flagName,
      $feature_flag_response: variant,
    });
  } catch (error) {
    console.error('[Analytics] Error tracking variant exposure:', error);
  }
}

/**
 * Reset analytics (e.g., on logout)
 */
export function resetAnalytics(): void {
  const ph = getPosthog();
  if (!ph) return;

  try {
    ph.reset();
    if (isDebugEnabled()) {
      console.log('[Analytics] Reset');
    }
  } catch (error) {
    console.error('[Analytics] Error resetting:', error);
  }
}

/**
 * Track page view in Google Analytics
 * Call from useEffect when route changes
 */
export function trackPageView(path: string, title?: string): void {
  if (typeof window === 'undefined' || !window.gtag) return;

  try {
    window.gtag('config', import.meta.env.VITE_GA_ID, {
      page_path: path,
      page_title: title || document.title,
    });

    if (isDebugEnabled()) {
      console.log('[GA] Page view:', { path, title });
    }
  } catch (error) {
    console.error('[GA] Error tracking page view:', error);
  }
}
